import { Fragment, ReactElement, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Formik, FormikProps } from 'formik';
import cn from 'classnames';

import cms from '../../../data/cms.json';
import config from '../../../config.json';
import styles from './Step7.module.scss';
import { QuestionProps } from '../../../types/initData';
import { resolveFieldText, resolveFieldType } from '../../../utils/externalDataHandling';
import { findQuestionHeaderId, findQuestionRange } from '../../../utils/formFiltering';
import { renderQuestionTree } from '../../../utils/renderQuestionTree';
import { ToggleField } from '../../ToggleField/ToggleField';
import { updateGlobalFormState } from '../../../utils/updateGlobalFormState';
import { scrollToError } from '../../../utils/scrollToError';
import { getFilledData } from '../../../utils/getFilledData';
import { getInitialValues } from '../../../utils/getInitialValues';
import { setCompleted } from '../../../utils/setCompleted';

import { ErrorForm } from '../../ErrorForm/ErrorForm';
import { Step } from '../../Step/Step';
import { Box } from '../../Box/Box';
import { Button } from '../../Button/Button';
import { Grid } from '../../Grid/Grid';
import { Label } from '../../Label/Label';
import { ValuesProps } from '../Step6/Step6';
import { FormItemError } from '../../FormItemError/FormItemError';
import { AppContext } from '../../../context/AppContext';
import { Loader } from '../../Loader/Loader';
import { KeyValueProps } from '../../../types/common';
import ToggleListWrapper from '../../ToggleListWrapper/ToggleListWrapper';

interface Step7Props {
    className?: string;
}

export const Step7 = ({ className }: Step7Props): ReactElement | null => {
    const stepCodeName = 'Step7';
    const navigate = useNavigate();
    const ctx = useContext(AppContext);
    const formikRef = useRef<FormikProps<any>>(null);
    const [questionsRange, setQuestionsRange] = useState<Array<number>>();
    const [currentStepId, setCurrentStepId] = useState<null | number>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialValues, setInitialValues] = useState<KeyValueProps | undefined>(undefined);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const initDataQ = ctx.initDataQuestionaire?.Questionnaires.Questions;

    useEffect(() => {
        localStorage.setItem('currentStep', '7');
        ctx.setIsBackButtonVisible(true);
    }, []);

    useEffect(() => {
        if (questionsRange && initDataQ) {
            const data = getFilledData(questionsRange, ctx);
            setInitialValues(data);
        }
    }, [questionsRange, initDataQ]);

    useEffect(() => {
        const step = findQuestionHeaderId(stepCodeName, ctx.initData?.Lovs?.QuestionHeaders);
        step && setCurrentStepId(step.Id);
    }, [ctx.initData?.Lovs]);

    useEffect(() => {
        if (currentStepId) {
            const range = findQuestionRange(currentStepId, initDataQ);
            range && setQuestionsRange(range);
        }
    }, [currentStepId, initDataQ]);

    useEffect(() => {
        if (isSubmitting) {
            setIsSubmitting(false);
        }
    }, [ctx.formResult, isSubmitting]);

    useEffect(() => {
        const values = formikRef?.current?.values;

        if (ctx.isSaving && values && questionsRange && ctx.formResult) {
            updateGlobalFormState(initDataQ?.slice(...questionsRange) || [], values, ctx, config.STEP_NAMES.STEP7);
            ctx.setIsSaving(false);
        }
    }, [ctx.isSaving]);

    return (
        <Step title={cms.step7.title} text={cms.step7.text} className={cn(styles.wrapper, className)}>
            {questionsRange && initDataQ && ctx.initData?.Lovs ? (
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues || getInitialValues(initDataQ.slice(...questionsRange)) || {}}
                    onSubmit={(values) => {
                        setIsSubmitting(true);
                        updateGlobalFormState(initDataQ.slice(...questionsRange), values, ctx, config.STEP_NAMES.STEP7);
                        setCompleted(7);
                        navigate(`/${config.SLUGS.STEP8_SLUG}/`);
                    }}
                    validateOnBlur={false}
                    validateOnChange={hasSubmitted}
                    validateOnMount={false}
                    enableReinitialize
                >
                    {({ values, errors, handleSubmit, setFieldValue }: ValuesProps) => (
                        <Form className={styles.form} data-test="stepForm">
                            <Box className={cn('mb-6', 'mb-3-m', 'question-box', styles.box)} data-test="questionsBox">
                                {initDataQ.slice(...questionsRange).map((formItem: QuestionProps, i: number) => (
                                    <Fragment key={`fragment-${i}`}>
                                        <div className="question-item">
                                            <Label
                                                htmlFor={formItem.Code}
                                                help={formItem.Help}
                                                data-test={`formItem[${formItem.Code}]Label`}
                                            >
                                                {formItem.Name}
                                            </Label>
                                            <Grid cols="auto">
                                                <ToggleListWrapper>
                                                    {formItem.PossibleAnswersIds.length > 0 && (
                                                        <div
                                                            className="toggle-list"
                                                            data-test={`formItem[${formItem.Code}]ToggleList`}
                                                        >
                                                            {formItem.PossibleAnswersIds.map((a: number, i: number) => (
                                                                <ToggleField
                                                                    key={`${formItem.Code}-${i}`}
                                                                    name={formItem.Code}
                                                                    id={`${formItem.Code}-${i}`}
                                                                    variant={resolveFieldType(
                                                                        formItem?.QuestionAnswerFormatId,
                                                                        ctx.initData?.Lovs
                                                                    )}
                                                                    value={
                                                                        resolveFieldText(a, ctx.initData?.Lovs) || ''
                                                                    }
                                                                    error={errors[formItem?.Code]}
                                                                    data-test={`formItem[${formItem.Code}]Toggle[${a}]`}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </ToggleListWrapper>
                                                {errors[formItem?.Code] && (
                                                    <FormItemError
                                                        text={errors[formItem?.Code]}
                                                        data-test={`formItem[${formItem.Code}]Error`}
                                                    />
                                                )}
                                            </Grid>
                                        </div>

                                        {ctx.initData?.Lovs &&
                                            renderQuestionTree(
                                                formItem,
                                                values,
                                                ctx.initData?.Lovs,
                                                errors,
                                                ctx,
                                                setFieldValue
                                            )}
                                    </Fragment>
                                ))}
                            </Box>

                            <Button
                                variant="primary"
                                loading={isSubmitting}
                                onClick={() => {
                                    setHasSubmitted(true);
                                    handleSubmit();
                                    scrollToError();
                                }}
                                data-test="submitButton"
                            >
                                {cms.common.continueButton}
                            </Button>
                        </Form>
                    )}
                </Formik>
            ) : (
                <Loader size="medium" data-test="loader" />
            )}

            <ErrorForm />
        </Step>
    );
};
